import React from 'react';
import tw, { styled } from 'twin.macro';

const Container = styled.div`
  ${tw`flex h-screen flex-col items-center justify-center bg-gray-50 px-4`}
`;

const ErrorBox = styled.div`
  ${tw`w-full max-w-2xl rounded-lg border border-red-200 bg-white p-8 shadow-lg`}
`;

const ErrorIcon = styled.div`
  ${tw`mb-4 text-6xl text-red-500`}
`;

const Title = styled.h1`
  ${tw`mb-4 text-2xl font-bold text-gray-900`}
`;

const Message = styled.p`
  ${tw`mb-6 text-gray-700`}
`;

const ErrorDetails = styled.details`
  ${tw`mb-6 cursor-pointer rounded border border-gray-200 bg-gray-50 p-4`}

  summary {
    ${tw`font-semibold text-gray-900`}
  }
`;

const ErrorText = styled.pre`
  ${tw`mt-4 overflow-auto rounded bg-gray-100 p-4 text-xs text-red-600`}
`;

const ButtonGroup = styled.div`
  ${tw`flex gap-3`}
`;

const Button = styled.button`
  ${tw`rounded bg-blue-500 px-6 py-3 text-white transition hover:bg-blue-600`}

  &.secondary {
    ${tw`bg-gray-200 text-gray-900 hover:bg-gray-300`}
  }
`;

/**
 * Error fallback UI component displayed when ErrorBoundary catches an error
 * @param {Object} props
 * @param {Error} props.error - The caught error
 * @param {Object} props.errorInfo - Additional error information
 * @param {Function} props.resetError - Function to reset the error state
 */
const ErrorFallback = ({ error, errorInfo, resetError }) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Container>
      <ErrorBox>
        <ErrorIcon>⚠️</ErrorIcon>
        <Title>Något gick fel</Title>
        <Message>
          Ett oväntat fel har inträffat i applikationen. Vi ber om ursäkt för
          besväret. Du kan försöka ladda om sidan eller gå tillbaka till
          startsidan.
        </Message>

        {error && (
          <ErrorDetails>
            <summary>Tekniska detaljer</summary>
            <ErrorText>
              <strong>Felmeddelande:</strong> {error.toString()}
              {errorInfo && errorInfo.componentStack && (
                <>
                  {'\n\n'}
                  <strong>Komponent Stack:</strong>
                  {errorInfo.componentStack}
                </>
              )}
            </ErrorText>
          </ErrorDetails>
        )}

        <ButtonGroup>
          <Button onClick={handleReload}>Ladda om sidan</Button>
          <Button className="secondary" onClick={handleGoHome}>
            Gå till startsidan
          </Button>
          {resetError && (
            <Button className="secondary" onClick={resetError}>
              Försök igen
            </Button>
          )}
        </ButtonGroup>
      </ErrorBox>
    </Container>
  );
};

export default ErrorFallback;
